import { useEffect, useState } from "react";
import type { HealthInsight, IngestionResource, SyncStatusRow } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { useConnectionContext } from "@/lib/connectionContext";
import { ChartLegend, HourlyChart, type HourlySeries } from "@/components/charts/HourlyChart";

type Loadable<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "error"; message: string };

const STALE_AFTER_SEC = 30 * 60;

const RESOURCE_LABELS: Record<IngestionResource, string> = {
  phone_numbers: "Phone numbers",
  messages: "Messages",
  calls: "Calls",
  errors: "Alerts",
};

/**
 * Insights → Health. "Is everything working?" Status view, not a trends view.
 * Three widgets: summary cards, 24h alert volume chart, sync status grid.
 */
export function HealthPage() {
  const ctx = useConnectionContext();
  const scope = ctx.allSelected ? undefined : ctx.selectedIds;
  const scopeKey = scope ? scope.join(",") : "all";

  const [insight, setInsight] = useState<Loadable<HealthInsight>>({
    status: "loading",
  });

  useEffect(() => {
    if (ctx.status !== "ready") return;
    let cancelled = false;
    setInsight({ status: "loading" });
    void api.insights
      .health(scope)
      .then((data) => {
        if (!cancelled) setInsight({ status: "ready", data });
      })
      .catch((err) => {
        if (!cancelled) {
          setInsight({
            status: "error",
            message: err instanceof ApiClientError ? err.message : "Failed to load",
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, scopeKey]);

  return (
    <div className="space-y-8">
      <header>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
          Insights
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Health
        </h1>
        <p className="mt-1 text-[13.5px] text-zinc-600 dark:text-zinc-400">
          {scopeLabel(ctx)} · Last 24 hours.
        </p>
      </header>

      {insight.status === "loading" && <LoadingState />}
      {insight.status === "error" && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {insight.message}
        </p>
      )}
      {insight.status === "ready" && (
        <>
          <SummaryRow data={insight.data} />
          <AlertsChart data={insight.data} />
          <SyncStatusGrid data={insight.data} />
          <AlertsByCodeTable data={insight.data} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function SummaryRow({ data }: { data: HealthInsight }) {
  const s = data.summary;
  const healthyCount = Math.max(0, s.connectionsTotal - s.connectionsUnhealthy);
  const cards: Array<{ label: string; value: string; hint: string; tone?: "ok" | "warn" }> = [
    {
      label: "Healthy connections",
      value: `${healthyCount} / ${s.connectionsTotal}`,
      hint:
        s.connectionsUnhealthy === 0
          ? "All accounts syncing"
          : `${s.connectionsUnhealthy} need attention`,
      tone: s.connectionsUnhealthy === 0 ? "ok" : "warn",
    },
    {
      label: "Open ingestion failures",
      value: formatInt(s.openFailures),
      hint: s.openFailures === 0 ? "No failed jobs" : "consecutiveFailures > 0",
      tone: s.openFailures === 0 ? "ok" : "warn",
    },
    {
      label: "Twilio alerts · 24h",
      value: formatInt(s.alerts24h),
      hint: s.alerts24h === 0 ? "All clear" : "From Monitor API",
      tone: s.alerts24h === 0 ? "ok" : "warn",
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none p-4 dark:ring-1 dark:ring-inset dark:ring-white/[0.02]"
        >
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
              {c.label}
            </div>
            {c.tone && <ToneDot tone={c.tone} />}
          </div>
          <div className="mt-1.5 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            {c.value}
          </div>
          <div className="mt-1 text-[11.5px] text-zinc-500">{c.hint}</div>
        </div>
      ))}
    </div>
  );
}

function ToneDot({ tone }: { tone: "ok" | "warn" }) {
  return (
    <span
      className={
        "h-2 w-2 rounded-full " +
        (tone === "ok"
          ? "bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.15)] dark:bg-emerald-400"
          : "bg-amber-500 shadow-[0_0_0_3px_rgba(251,191,36,0.15)] dark:bg-amber-400")
      }
    />
  );
}

// ---------------------------------------------------------------------------

function AlertsChart({ data }: { data: HealthInsight }) {
  const series: HourlySeries[] = [
    {
      label: "Errors",
      fill: "rgb(251 113 133 / 0.9)",
      values: data.alertsHourly.map((b) => b.error),
    },
    {
      label: "Warnings",
      fill: "rgb(251 191 36 / 0.85)",
      values: data.alertsHourly.map((b) => b.warning),
    },
    {
      label: "Notices",
      // Slightly darker so it remains visible on the white card surface.
      fill: "rgb(113 113 122 / 0.75)",
      values: data.alertsHourly.map((b) => b.notice),
    },
  ];
  return (
    <section className="rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none p-5 dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <header className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-[13.5px] font-semibold text-zinc-900 dark:text-zinc-100">
            Twilio alerts · last 24h
          </h2>
          <p className="text-[11.5px] text-zinc-500">
            From the Monitor API · stacked by log level.
          </p>
        </div>
        <ChartLegend series={series} />
      </header>
      <HourlyChart
        hours={data.alertsHourly.map((b) => b.hour)}
        series={series}
        ariaLabel="Hourly Twilio alert volume over the last 24 hours"
      />
    </section>
  );
}

// ---------------------------------------------------------------------------

function SyncStatusGrid({ data }: { data: HealthInsight }) {
  if (data.syncStatus.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/30 p-6 text-center text-[13px] text-zinc-600 dark:text-zinc-400">
        No connections in scope.
      </div>
    );
  }
  // Group rows by connection so the layout reads as: account → 4 resource pills.
  const byConn = new Map<string, { name: string; rows: SyncStatusRow[] }>();
  for (const row of data.syncStatus) {
    const slot = byConn.get(row.connectionId);
    if (slot) slot.rows.push(row);
    else byConn.set(row.connectionId, { name: row.connectionName, rows: [row] });
  }

  return (
    <section>
      <header className="mb-2">
        <h2 className="text-[13.5px] font-semibold text-zinc-900 dark:text-zinc-100">
          Sync status
        </h2>
        <p className="text-[11.5px] text-zinc-500">
          Per-resource state for every connected Twilio account.
        </p>
      </header>
      <div className="space-y-3">
        {[...byConn.values()].map((c) => (
          <div
            key={c.name}
            className="rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none p-4 dark:ring-1 dark:ring-inset dark:ring-white/[0.02]"
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
                {c.name}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {c.rows.map((row) => (
                <ResourceTile key={row.resource} row={row} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ResourceTile({ row }: { row: SyncStatusRow }) {
  const tone = resolveTone(row);
  return (
    <div
      className={
        "rounded-lg border p-3 transition " +
        (tone === "ok"
          ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/[0.04]"
          : tone === "warn"
            ? "border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/[0.05]"
            : tone === "fail"
              ? "border-rose-200 bg-rose-50 dark:border-rose-500/30 dark:bg-rose-500/[0.05]"
              : "border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/40")
      }
    >
      <div className="flex items-center justify-between">
        <span className="text-[12.5px] font-medium text-zinc-900 dark:text-zinc-100">
          {RESOURCE_LABELS[row.resource]}
        </span>
        <ToneDot tone={tone === "fail" ? "warn" : tone === "warn" ? "warn" : "ok"} />
      </div>
      <div className="mt-1 text-[11px] text-zinc-500">
        {row.lastSyncedAt ? `Last sync ${formatRelative(row.lastSyncedAt)}` : "Never synced"}
      </div>
      {row.consecutiveFailures > 0 && (
        <div className="mt-1 text-[11px] text-rose-700 dark:text-rose-300/80">
          {row.consecutiveFailures} consecutive failure
          {row.consecutiveFailures === 1 ? "" : "s"}
          {row.lastErrorCode ? ` · ${row.lastErrorCode}` : ""}
        </div>
      )}
      {row.consecutiveFailures === 0 && row.status === "running" && (
        <div className="mt-1 text-[11px] text-sky-700 dark:text-sky-300/80">Running…</div>
      )}
    </div>
  );
}

function resolveTone(row: SyncStatusRow): "ok" | "warn" | "fail" | "neutral" {
  if (row.consecutiveFailures > 0 || row.status === "error") return "fail";
  if (row.lastSyncedAt === null) return "neutral";
  if (row.ageSeconds !== null && row.ageSeconds > STALE_AFTER_SEC) return "warn";
  return "ok";
}

// ---------------------------------------------------------------------------

function AlertsByCodeTable({ data }: { data: HealthInsight }) {
  if (data.alertsByCode.length === 0) return null;
  return (
    <section>
      <header className="mb-2 flex items-end justify-between">
        <div>
          <h2 className="text-[13.5px] font-semibold text-zinc-900 dark:text-zinc-100">
            Top alert codes · 24h
          </h2>
          <p className="text-[11.5px] text-zinc-500">
            Twilio Monitor API alerts grouped by error code.
          </p>
        </div>
        <a
          href="https://www.twilio.com/docs/api/errors"
          target="_blank"
          rel="noreferrer"
          className="text-[11.5px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
        >
          Twilio error reference →
        </a>
      </header>
      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
        <table className="w-full text-left text-sm">
          <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider dark:bg-zinc-900/60 text-zinc-500">
            <tr>
              <th className="px-4 py-2.5">Code</th>
              <th className="px-4 py-2.5">More info</th>
              <th className="px-4 py-2.5 text-right">Count</th>
              <th className="px-4 py-2.5 text-right">Share</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
            {data.alertsByCode.map((a) => (
              <tr
                key={a.errorCode}
                className="transition hover:bg-zinc-50 dark:hover:bg-zinc-900/60"
              >
                <td className="px-4 py-3 align-top">
                  <a
                    href={`https://www.twilio.com/docs/api/errors/${a.errorCode}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-[12.5px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
                  >
                    {a.errorCode}
                  </a>
                </td>
                <td className="max-w-[40ch] px-4 py-3 align-top text-[12px]">
                  {a.sampleMoreInfo ? (
                    <a
                      href={a.sampleMoreInfo}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-zinc-600 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
                    >
                      {a.sampleMoreInfo}
                    </a>
                  ) : (
                    <span className="text-zinc-400 dark:text-zinc-600">—</span>
                  )}
                </td>
                <td className="px-4 py-3 align-top text-right font-mono text-[12.5px] text-zinc-800 dark:text-zinc-200">
                  {formatInt(a.count)}
                </td>
                <td className="px-4 py-3 align-top text-right">
                  <span className="font-mono text-[11.5px] text-zinc-600 dark:text-zinc-400">
                    {Math.round(a.share * 100)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-[88px] animate-pulse rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none"
          />
        ))}
      </div>
      <div className="h-[200px] animate-pulse rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none" />
    </div>
  );
}

// ---------------------------------------------------------------------------

function scopeLabel(ctx: ReturnType<typeof useConnectionContext>): string {
  if (ctx.connections.length === 0) return "No accounts connected yet";
  if (ctx.allSelected)
    return `Across all ${ctx.connections.length} ${ctx.connections.length === 1 ? "account" : "accounts"}`;
  if (ctx.selectedIds.length === 1) {
    const c = ctx.connections.find((x) => x.id === ctx.selectedIds[0]);
    return `Scoped to ${c?.friendlyName ?? c?.accountSid ?? "1 account"}`;
  }
  return `Scoped to ${ctx.selectedIds.length} of ${ctx.connections.length} accounts`;
}

function formatInt(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
